/**
 * Build the menu for the base config. Extensions can add to this.
 */

import { Plugin, PluginKey, Transaction } from "prosemirror-state";
import { StekzMenuBar } from "./menu-bar";

import { wrapInList } from "prosemirror-schema-list";
import { autoJoin, chainCommands, setBlockType } from "prosemirror-commands";
import { MenuItemSpec, undoItem, redoItem } from "prosemirror-menu";
import { NodeType, MarkType } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { EditorState } from "prosemirror-state";
import { buildMenuItems } from "prosemirror-example-setup";

import {
    convertToListType,
    getNearestListNode,
    indent,
    unindent,
} from "@/wc/text-editor/lib/core/commands.js";

interface Icon {
    name: string;
    library?: string;
}

export interface MenuItem {
    // Internal name
    name: string;
    // The text shown in the menu.
    label: string | ((state: EditorState) => string);
    // Whether the label is shown in the menu bar.
    showLabel?: boolean;
    // Whether to show it in the inline menu.
    showInInlineMenu?: boolean;
    // The group in which the item is displayed.
    group: string;
    // Menu items are sorted by order within their group.
    order?: number;
    // Dropdown opens a list of items. Toggle is a single button.
    type: "toggle" | "dropdown";
    // The icon to show with the button.
    icon?: Icon | ((state: EditorState) => Icon);
    // Whether to focus on the editor after toggling.
    refocus?: boolean;
    // Called whenever the item is clicked.
    run?: (
        state: EditorState,
        dispatch: (tr: Transaction) => void,
        view: EditorView,
        event: Event
    ) => void;
    active?: (state: EditorState) => boolean;
    disabled?: (state: EditorState) => boolean;
    // Dropdown items, only applicable if type is dropdown.
    items?: Array<Omit<MenuItem, "group" | "showInInlineMenu">>;
    // Get items through an async function. Use either
    // this or items for dropdown menu, not both.
    getItems?: () => Promise<MenuItem["items"]>;
    // Whether to show a caret at the end of the button.
    // Useful for dropdown menu type.
    caret?: boolean;
}

export interface MenuItemState
    extends Omit<
        MenuItem,
        "run" | "label" | "icon" | "active" | "disabled" | "items" | "getItems"
    > {
    label: string;
    icon: Icon;
    run?: (event: Event) => void;
    active?: boolean;
    disabled?: boolean;
    items?: Array<Omit<MenuItemState, "group" | "showInInlineMenu">>;
    getItems?: () => Promise<MenuItemState["items"]>;
}

export type MenuFunction = (view: EditorView) => Array<MenuItem>;
export type MenuStateFunction = (view: EditorView) => Array<MenuItemState>;

//
/**
 * Toggle a Prosemirror list inside the editor.
 */
const toggleList = (listType: NodeType) =>
    autoJoin(chainCommands(convertToListType(listType), wrapInList(listType)), [
        listType.name,
    ]);

export const recursiveMenuItemToState = <M extends MenuItem>(
    item: M,
    state: EditorState,
    view: EditorView,
    runFunc?: (
        originalRun: M["run"]
    ) => (event: Event, ...args: unknown[]) => void
): MenuItemState => {
    return {
        ...item,
        label:
            typeof item.label === "function" ? item.label(state) : item.label,
        icon: typeof item.icon === "function" ? item.icon(state) : item.icon,
        items: item.items
            ? item.items.map((subItem) => {
                  return recursiveMenuItemToState(
                      subItem as MenuItem,
                      state,
                      view,
                      runFunc
                  );
              })
            : undefined,
        getItems: item.getItems
            ? async () => {
                  const subItems = await item.getItems();
                  return subItems.map((subItem) => {
                      return recursiveMenuItemToState(
                          subItem as MenuItem,
                          state,
                          view,
                          runFunc
                      );
                  });
              }
            : undefined,
        run: runFunc
            ? runFunc(item.run)
            : (event) => {
                  item.run(state, view.dispatch, view, event);
              },
        active: item.active?.(state),
        disabled: item.disabled?.(state),
    } as MenuItemState;
};

/**
 * Helper to quickly generate a toolbar item.
 */
export const toolbarItem = (
    spec: MenuItemSpec,
    icon: string,
    refocus = true
): Omit<MenuItem, "name" | "group" | "order"> => {
    return {
        run: (state, dispatch, view, event) => {
            spec.run(state, dispatch, view, event);
            if (refocus) {
                view.focus();
            }
        },
        icon: () => ({ name: icon, library: "component-assets" }),
        label: spec.title,
        disabled: (state) => {
            if (!spec.enable) {
                return false;
            }
            return !spec.enable(state);
        },
        active: spec.active,
        type: "toggle",
    };
};

/**
 * Copied from prosemirror-example-setup internals.
 */
export const markActive = (state: EditorState, type: MarkType) => {
    const { from, $from, to, empty } = state.selection;
    if (empty) return !!type.isInSet(state.storedMarks || $from.marks());
    else return state.doc.rangeHasMark(from, to, type);
};

interface MenuPluginState {
    items: MenuItem[];
    menuBar?: StekzMenuBar;
}

export const MENU_PLUGIN_KEY = new PluginKey<MenuPluginState>("MENU_PLUGIN");

export function menuPlugin() {
    return new Plugin<MenuPluginState>({
        key: MENU_PLUGIN_KEY,

        state: {
            init(config) {
                const menuItems = buildMenuItems(config.schema!);
                return {
                    items: [
                        {
                            name: "undo",
                            ...toolbarItem(
                                undoItem.spec,
                                "arrow-counterclockwise"
                            ),
                            label: "Undo",
                            order: 10,
                            group: "base",
                        },
                        {
                            name: "redo",
                            ...toolbarItem(redoItem.spec, "arrow-clockwise"),
                            label: "Redo",
                            order: 11,
                            group: "base",
                        },
                        {
                            name: "bold",
                            ...toolbarItem(
                                menuItems.toggleStrong!.spec,
                                "type-bold"
                            ),
                            label: "Bold",
                            order: 20,
                            group: "base",
                        },
                        {
                            name: "italic",
                            ...toolbarItem(
                                menuItems.toggleEm!.spec,
                                "type-italic"
                            ),
                            label: "Italic",
                            order: 25,
                            group: "base",
                        },
                        {
                            name: "code",
                            ...toolbarItem(menuItems.toggleCode!.spec, "code"),
                            label: "Code",
                            order: 30,
                            group: "base",
                        },
                        {
                            name: "code_block",
                            ...toolbarItem(
                                {
                                    ...menuItems.toggleCode!.spec,
                                    run: (state, dispatch, view) => {
                                        setBlockType(
                                            config.schema!.nodes.code_block
                                        )(state, dispatch, view);
                                        view.focus();
                                    },
                                },
                                "code_block"
                            ),
                            icon: () => ({
                                name: "braces",
                                library: "component-assets",
                            }),
                            label: "Code Block",
                            order: 35,
                            group: "base",
                        },
                        {
                            name: "unordered-list",
                            ...toolbarItem(
                                menuItems.wrapBulletList!.spec,
                                "list-ul"
                            ),
                            label: "Bulleted list",
                            order: 40,
                            group: "base",
                            active: (state) =>
                                getNearestListNode(state)?.node?.type?.name ===
                                "bullet_list",
                            run: (state, dispatch, view) => {
                                autoJoin(
                                    toggleList(state.schema.nodes.bullet_list),
                                    ["ordered_list"]
                                )(state, dispatch, view);
                                view.focus();
                            },
                        },
                        {
                            name: "ordered-list",
                            ...toolbarItem(
                                menuItems.wrapOrderedList!.spec,
                                "list-ol"
                            ),
                            label: "Numbered list",
                            order: 45,
                            group: "base",
                            active: (state) =>
                                getNearestListNode(state)?.node?.type?.name ===
                                "ordered_list",
                            run: (state, dispatch, view) => {
                                autoJoin(
                                    toggleList(
                                        view.state.schema.nodes.ordered_list
                                    ),
                                    ["ordered_list"]
                                )(state, dispatch, view);
                                view.focus();
                            },
                        },
                        {
                            name: "unindent",
                            ...toolbarItem(
                                menuItems.wrapOrderedList!.spec,
                                "unindent"
                            ),
                            order: 50,
                            group: "base",
                            label: "Unindent",
                            disabled: (state) => !unindent(state.schema),
                            run: (state, dispatch, view) => {
                                unindent(state.schema)(state, dispatch);
                                view.focus();
                            },
                        },
                        {
                            name: "indent",
                            ...toolbarItem(
                                menuItems.wrapBulletList!.spec,
                                "indent"
                            ),
                            label: "Indent",
                            order: 55,
                            group: "base",
                            disabled: (state) => !indent(state.schema),
                            run: (state, dispatch, view) => {
                                indent(state.schema)(state, dispatch);
                                view.focus();
                            },
                        },
                    ],
                };
            },
            apply(tr, pluginState) {
                if (tr.getMeta(MENU_PLUGIN_KEY)) {
                    return {
                        ...pluginState,
                        items: tr.getMeta(MENU_PLUGIN_KEY),
                    };
                }
                return pluginState;
            },
        },

        view(view) {
            const pluginState = MENU_PLUGIN_KEY.getState(view.state);
            if (!pluginState || !view.dom.parentNode) {
                return {};
            }
            if (!pluginState.menuBar) {
                pluginState.menuBar = view.dom.parentNode.insertBefore(
                    new StekzMenuBar(),
                    view.dom
                );
            }

            if (view.editable) {
                pluginState.menuBar.items = pluginState.items.map((item) => {
                    return recursiveMenuItemToState(item, view.state, view);
                });
            } else {
                pluginState.menuBar.items = [];
            }

            return {
                update(view, newState) {
                    if (!pluginState.menuBar) {
                        return;
                    }
                    if (view.editable) {
                        pluginState.menuBar.items = pluginState.items.map(
                            (item) => {
                                return recursiveMenuItemToState(
                                    item,
                                    newState,
                                    view
                                );
                            }
                        );
                    } else {
                        pluginState.menuBar.items = [];
                    }
                },
            };
        },
    });
}
